import {
  AfterViewInit,
  Component,
  ElementRef,
  HostBinding,
  HostListener,
  Input,
  inject,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { HttpClientModule } from '@angular/common/http';
import { CommonModule } from '@angular/common';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute } from '@angular/router';
import { Observable, Subscription, of } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { WidgetRendererComponent } from '../widget-renderer/widget-renderer.component';
import { GlobalFilterComponent } from '@shared-lib/global-components/global-filter/global-filter.component';
import { ConfigService } from '@shared-lib/shared-services/config.service';
import { GlobalFilterService } from '@shared-lib/shared-services/global-filter-service';
import { DashboardTitleService } from '@shared-lib/shared-services/dashboard-title.service';
import { PlatformService } from '@shared-lib/shared-services/platform.service.service';
import { ResponsiveLayoutService } from '@shared-lib/shared-services/responsive-layout.service';
import * as echarts from 'echarts';
import { ThemeService } from '@shared-lib/shared-services/theme.service';
import { LayoutService } from '@shared-lib/shared-services/layout.service';
import { CapitalCasePipe } from '@shared-lib/pipes/capital-case.pipe';
import { CustomGlobalFilterService } from '@custom';
import { RoleVisibilityService } from '../shared/role-visibility/role-visibility.service';
import { KeycloakService } from 'keycloak-angular';

interface GridItem {
  id: string;
  x: number;
  y: number;
  w: number;
  h: number;
  minW: number;
  maxW: number;
  minH: number;
  maxH: number;
  locked: boolean;
}

interface WidgetRec {
  id: string;
  widget: any;
}

interface Interaction {
  mode: 'drag' | 'resize';
  id: string;
  corner?: 'nw' | 'ne' | 'sw' | 'se';
  pointerId: number;
  startClientX: number;
  startClientY: number;
  startX: number;
  startY: number;
  startW: number;
  startH: number;
}

interface FocusModeSettings {
  enabled: boolean;
  includeTypes: Set<string>;
  excludeTypes: Set<string>;
}

interface TooltipSnapshotEntry {
  appendToBody?: boolean;
  appendTo?: unknown;
}

interface CompactViewportSnapshot {
  width: number;
  height: number;
  scale: number;
  requiredHeight: number;
}

interface PersistedLayoutSnapshot {
  version: number;
  layouts: Record<string, GridItem[]>;
}

interface LayoutPersistenceRequestConfig {
  apiUrl?: string;
  method?: string;
  dashboard_id?: string;
  dashboardId?: string;
  requestData?: Record<string, unknown>;
  defaultRequestData?: Record<string, unknown>;
  queryParams?: Record<string, unknown>;
  headers?: Record<string, string>;
  useQueryParams?: boolean;
  includeRequestContext?: boolean;
  responsePath?: string;
  layoutKey?: string;
  skipDefaultPayload?: boolean;
  customPayload?: Record<string, unknown>;
  entity?: string;
  workflowId?: string;
  functionName?: string;
  version?: string;
  communicationType?: string;
  enabled?: boolean;
}

interface LayoutPersistenceConfig extends LayoutPersistenceRequestConfig {
  load?: Partial<LayoutPersistenceRequestConfig>;
  save?: Partial<LayoutPersistenceRequestConfig>;
  emailOwnerMode?: boolean;
  allowDefaultSave?: boolean;
  adminEmails?: string[];
  adminRoles?: string[];
  fallbackAdminEmails?: string[];
  fallbackAdminEmail?: string;
  personalSaveLabel?: string;
  defaultSaveLabel?: string;
}

interface LayoutUserContext {
  userId: string | null;
  username: string | null;
  email: string | null;
  roleIds: string[];
}

interface PersistedLayoutRecord {
  emailId: string | null;
  uuid: string | null;
  role: string | null;
  sourceEmail: string;
  isFallback: boolean;
  raw: any;
}

interface PersistedLayoutLookupResult {
  layouts: Record<string, GridItem[]> | null;
  record: PersistedLayoutRecord | null;
}

type LayoutPersistenceScope = 'USER' | 'GLOBAL';
type LayoutPersistenceRequestMode = LayoutPersistenceScope | 'RESOLVE';

const BPS: Record<string, number> = { xl: 1536, lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 };
const COLS: Record<string, number> = { xl: 12, lg: 12, md: 10, sm: 6, xs: 4, xxs: 2 };
type DashboardNamePosition = 'top' | 'below';
const NON_FOCUS_WIDGET_TYPES = new Set<string>([
  'slider',
  'input-box',
  'configurable-form',
  'editable-table',
  'table',
  'dynamic-edit-table',
  'upload-file',
  'notes',
  'static-screen',
  'multiselect-toggle',
  'generic-table',
  'table-generic',
  'number-stats',
  'numberstatscomponent',
  'range-bidirectional-slider',
  'entity-table',
  'entity-form',
  'button',
  'landing-module-card',
  'json-driven-smart-table',
  'scenario-planner-table',
]);

@Component({
  selector: 'app-dashboard',
  imports: [
    HttpClientModule,
    WidgetRendererComponent,
    CommonModule,
    GlobalFilterComponent,
    CapitalCasePipe,
    MatTooltipModule,
  ],
  standalone: true,
  templateUrl: './dashboard.component.html',
  styleUrl: './dashboard.component.scss',
  providers: [
    { provide: GlobalFilterService, useClass: CustomGlobalFilterService },
    ResponsiveLayoutService,
  ],
})
export class DashboardComponent implements OnInit, AfterViewInit, OnDestroy {
  private static readonly COMPACT_BASELINE_BROWSER_ZOOM_MAX = 1.02;
  private static readonly COMPACT_EXIT_MARGIN_PX = 24;
  private static readonly COMPACT_ENTER_MARGIN_PX = 72;

  @ViewChild('dashboardContent') dashboardContentRef?: ElementRef<HTMLElement>;
  @ViewChild('gridContainer') gridContainerRef?: ElementRef<HTMLElement>;
  @Input() configFile?: string;
  @Input() dashboardSourceKey?: string;
  @HostBinding('class.no-scroll') get noScrollClass() {
    return !this.responsiveLayoutService.effectiveScreenFloating;
  }
  @HostBinding('class.focus-active') get focusActiveClass() {
    return !!this.focusedWidgetId;
  }

  configurations: any = {};
  screenFloating: boolean = true;
  configFileName!: string;
  sourceKey = 'dashboard';
  widgets: WidgetRec[] = [];
  widgetMap = new Map<string, WidgetRec>();
  layouts: Record<string, GridItem[]> = {};
  hiddenWidgetIds = new Set<string>();
  currentBp = 'lg';
  containerWidth = 0;
  activeDragId: string | null = null;
  activeResizeId: string | null = null;
  focusedWidgetId: string | null = null;
  focusMode: FocusModeSettings = {
    enabled: false,
    includeTypes: new Set<string>(),
    excludeTypes: new Set<string>(),
  };
  editMode = false;
  layoutEditEnabled = false;
  grid = {
    version: 1,
    breakpoints: { ...BPS },
    columns: { ...COLS },
    rowHeight: 72,
    gap: 12,
    draggable: true,
    resizable: true,
    persist: true,
    persistKey: '',
    defaultW: 4,
    defaultH: 4,
    defaultMinW: 2,
    defaultMinH: 2,
    defaultMaxH: 16,
    rawLayouts: {} as Record<string, Array<Record<string, unknown>>>,
  };

  private subs = new Subscription();
  private layoutService = inject(LayoutService);
  /** Untouched response from the most recent loadCharts/loadDashboardFromApi.
   *  Held so the LayoutService can reshape its pageContent on layout switches
   *  without re-fetching the dashboard. */
  private originalDashboardConfig: any = null;
  /** JSON snapshot of the last applied pageContent — lets the layout
   *  subscription skip re-renders when the resolved layout is unchanged. */
  private appliedLayoutSignature: string | null = null;
  private interaction?: Interaction;
  private interactionTarget?: HTMLElement;
  private queued: { x: number; y: number } | null = null;
  private raf = false;
  private gridObserver?: ResizeObserver;
  private tooltipAppendSnapshot = new WeakMap<HTMLElement, TooltipSnapshotEntry[]>();
  private focusTooltipSyncTimer?: ReturnType<typeof setInterval>;
  private compactFitRafId: number | null = null;
  private compactScrollLock = false;
  private compactScrollLockSnapshot: CompactViewportSnapshot | null = null;
  private navFiltersApplied = false;
  private persistedLayouts: Record<string, GridItem[]> | null = null;
  private persistedLayoutRecord: PersistedLayoutRecord | null = null;
  private editSessionLayouts: Record<string, GridItem[]> | null = null;

  get effectiveScreenFloating(): boolean {
    return this.responsiveLayoutService.effectiveScreenFloating;
  }

  get isInteracting(): boolean {
    return !!this.interaction;
  }

  get hasFocusWidget(): boolean {
    return !!this.focusedWidgetId;
  }

  get orderedBps(): string[] {
    return Object.entries(this.grid.breakpoints).sort((a, b) => b[1] - a[1]).map(([k]) => k);
  }

  get layoutSavePersonalLabel(): string {
    return this.resolveLayoutPersistenceConfig(this.configurations)?.personalSaveLabel
      || 'Save My Layout';
  }

  get layoutSaveDefaultLabel(): string {
    return this.resolveLayoutPersistenceConfig(this.configurations)?.defaultSaveLabel
      || 'Save As Default';
  }

  get canSaveDefaultLayout(): boolean {
    if (!this.editMode || !this.layoutEditEnabled) return false;

    const persistence = this.resolveLayoutPersistenceConfig(this.configurations);
    if (!persistence || persistence.allowDefaultSave === false) return false;

    const userRoles = new Set(
      this.getCurrentLayoutUserContext().roleIds.map((role) => role.trim().toLowerCase())
    );
    if (!userRoles.size) return false;

    const configuredAdminRoles = (persistence.adminRoles || [])
      .map((role) => String(role || '').trim().toLowerCase())
      .filter((role) => !!role);

    if (configuredAdminRoles.length) {
      return configuredAdminRoles.some((role) => userRoles.has(role));
    }

    return [...userRoles].some((role) => role.includes('admin'));
  }

  get cols(): number {
    return this.grid.columns[this.currentBp] ?? 12;
  }

  get gridStyle(): Record<string, string> {
    return {
      'grid-template-columns': `repeat(${this.cols}, minmax(0, 1fr))`,
      'grid-auto-rows': `${this.grid.rowHeight}px`,
      // Custom Theme tab's "Widget spacing" choice writes --dvm-widget-gap;
      // when unset, fall back to the dashboard JSON's own gap value.
      gap: `var(--dvm-widget-gap, ${this.grid.gap}px)`,
    };
  }

  get dashboardNamePosition(): DashboardNamePosition {
    return this.normalizeDashboardNamePosition(
      this.configurations?.dashboardNamePosition ??
      this.configurations?.['dashboardNamePosition'] ??
      this.configurations?.['dashboard-name-position']
    );
  }

  get showDashboardNameOnScreen(): boolean {
    return this.dashboardNamePosition === 'below';
  }

  get showDashboardNameInNavbar(): boolean {
    return this.dashboardNamePosition === 'top';
  }

  get dashboardNameFontSize(): string {
    return this.normalizeDashboardNameFontSize(
      this.configurations?.dashboardNameFontSize ??
      this.configurations?.['dashboard-name-font-size'] ??
      this.configurations?.['dashboardNamefontsize'] ??
      this.configurations?.['dashboardNAmefontsize']
    );
  }

  get viewItems(): Array<{ item: GridItem; rec: WidgetRec }> {
    const l = this.layouts[this.currentBp] ?? [];
    return l
      .filter((item) => !this.hiddenWidgetIds.has(item.id))
      .map((item) => {
        const rec = this.widgetMap.get(item.id);
        return rec ? { item, rec } : null;
      })
      .filter((x): x is { item: GridItem; rec: WidgetRec } => !!x);
  }

  constructor(
    private hostRef: ElementRef<HTMLElement>,
    public configService: ConfigService,
    private route: ActivatedRoute,
    private globalFilterService: GlobalFilterService,
    private platformService: PlatformService,
    private responsiveLayoutService: ResponsiveLayoutService,
    private themeService: ThemeService,
    private dashboardTitleService: DashboardTitleService,
    private roleVisibilityService: RoleVisibilityService,
    private keycloakService: KeycloakService,
  ) { }

  ngOnInit(): void {
    this.responsiveLayoutService.init();

    this.subs.add(
      this.responsiveLayoutService.reflow$.subscribe(() => {
        if (this.configurations?.pageContent?.length) {
          this.applyWidgetHeightsInPlace(this.configurations);
        }
        this.queueCompactFitMeasurement();
      })
    );

    // Load the layout manifest in parallel with the dashboard config — once
    // it arrives, re-resolve the active layout so a saved selection (from
    // localStorage) is honored on first paint, not just after the user picks.
    this.subs.add(
      this.layoutService.loadManifest().subscribe(() => this.maybeReapplyLayout())
    );

    // React to layout switches from the Theme Options dialog: when the user
    // hits "Apply & Preview" or "Clear Layout", reshape the current dashboard
    // and re-run the grid pipeline. The first emission (BehaviorSubject seed)
    // is a no-op because originalDashboardConfig isn't loaded yet.
    this.subs.add(
      this.layoutService.activeLayoutId$.subscribe(() => this.maybeReapplyLayout())
    );

    // Seed filters from navigation before any widget API call starts.
    this.globalFilterService.updateAllFilters(this.buildInitialFiltersFromNavigation());
    this.globalFilterService.activeFilter = null;
    this.globalFilterService.resetFilterSelection('customer_spec');

    const data = this.route.snapshot.data;
    const inputConfigFile = (this.configFile || '').trim();
    if (inputConfigFile || data['configFile']) {
      this.configFileName = inputConfigFile || data['configFile'];
      this.sourceKey = this.dashboardSourceKey || this.configFileName;
      this.loadCharts();
    } else {
      const appId = this.route.snapshot.data['dvmWorkflowAppId'];
      if (appId) {
        this.sourceKey = appId;
        this.loadDashboardFromApi(environment.PROJECT_ID, appId);
      }
    }
  }

  ngAfterViewInit(): void {
    this.responsiveLayoutService.startChartAutoResize();
    this.gridObserver = new ResizeObserver(() => {
      this.onViewportChange();
      this.queueCompactFitMeasurement();
    });
    if (this.dashboardContentRef?.nativeElement) this.gridObserver.observe(this.dashboardContentRef.nativeElement);
    if (this.gridContainerRef?.nativeElement) this.gridObserver.observe(this.gridContainerRef.nativeElement);
    this.onViewportChange();
    this.queueCompactFitMeasurement();
  }

  ngOnDestroy(): void {
    this.finishInteraction(false);
    this.gridObserver?.disconnect();
    if (this.compactFitRafId !== null) {
      cancelAnimationFrame(this.compactFitRafId);
      this.compactFitRafId = null;
    }

    // ✅ from other branch
    this.themeService.clearDashboardOverrides();

    this.responsiveLayoutService.stopChartAutoResize();
    this.subs.unsubscribe();

    this.stopFocusTooltipSync();
    void this.exitFocusMode();
    this.syncFocusBodyClass();

    this.responsiveLayoutService.setScreenFloating(true);
    this.responsiveLayoutService.setCompactLayoutFitOverride(null);
    this.compactScrollLock = false;
    this.compactScrollLockSnapshot = null;

    this.globalFilterService.clearAllFilters();
    this.globalFilterService.activeFilter = null;
    this.dashboardTitleService.clearDashboardTitle();
  }

  /**
   * Seed GlobalFilterService from navigation query params + router state payload.
   * This enables cross-page payload handoff (e.g. navigateAction.stateKey payload)
   * without requiring each destination widget/component to read Router state.
   */
  private buildInitialFiltersFromNavigation(): {
    queryParams: Record<string, any>;
    requestParams: Record<string, any>;
    widgetId: string | null;
    filterListener?: string[];
    sourceWidgetId?: string | null;
  } {
    const queryParams = { ...(this.route?.snapshot?.queryParams || {}) } as Record<string, any>;
    const requestParams: Record<string, any> = {};
    const navState: any = (window.history?.state || {}) as any;
    let filterListener: string[] | undefined;
    let sourceWidgetId: string | null = null;

    // Common pattern used in dashboard configs:
    // navigateAction: { stateKey: "crossNavPayload", payload: { requestData: {...} } }
    const crossNavPayload = navState?.crossNavPayload;
    if (crossNavPayload && typeof crossNavPayload === 'object') {
      if (crossNavPayload.requestData && typeof crossNavPayload.requestData === 'object') {
        Object.assign(requestParams, crossNavPayload.requestData);
      }
      if (crossNavPayload.queryParams && typeof crossNavPayload.queryParams === 'object') {
        Object.assign(queryParams, crossNavPayload.queryParams);
      }
      if (crossNavPayload.requestParams && typeof crossNavPayload.requestParams === 'object') {
        Object.assign(requestParams, crossNavPayload.requestParams);
      }
      Object.assign(requestParams, this.extractLooseRequestParams(crossNavPayload));
      filterListener = this.normalizeNavigationListener(
        crossNavPayload.filterListener ??
        crossNavPayload.listenerWidget ??
        crossNavPayload.listenerWidgets ??
        crossNavPayload.ListenerWidget ??
        crossNavPayload.ListenerWidgets ??
        crossNavPayload.listnerWidget ??
        crossNavPayload.listnerWidgets ??
        crossNavPayload.ListnerWidget ??
        crossNavPayload.ListnerWidgets ??
        crossNavPayload.Listnerwidget,
      );
      sourceWidgetId = this.firstString(crossNavPayload.sourceWidgetId, queryParams['sourceWidgetId']);
    }

    // Also support direct state usage for flexibility.
    if (navState?.requestParams && typeof navState.requestParams === 'object') {
      Object.assign(requestParams, navState.requestParams);
    }
    if (navState?.queryParams && typeof navState.queryParams === 'object') {
      Object.assign(queryParams, navState.queryParams);
    }
    const directListener = this.normalizeNavigationListener(
      navState?.filterListener ??
      navState?.listenerWidget ??
      navState?.listenerWidgets ??
      navState?.ListenerWidget ??
      navState?.ListenerWidgets ??
      navState?.listnerWidget ??
      navState?.listnerWidgets ??
      navState?.ListnerWidget ??
      navState?.ListnerWidgets ??
      navState?.Listnerwidget,
    );
    if (directListener?.length) {
      filterListener = directListener;
    }
    sourceWidgetId = this.firstString(sourceWidgetId, navState?.sourceWidgetId, queryParams['sourceWidgetId']);

    return {
      queryParams,
      requestParams,
      widgetId: null,
      ...(filterListener?.length ? { filterListener } : {}),
      ...(sourceWidgetId ? { sourceWidgetId } : {}),
    };
  }

  private normalizeNavigationListener(rawValue: unknown): string[] | undefined {
    const rawList = Array.isArray(rawValue)
      ? rawValue
      : typeof rawValue === 'string'
        ? rawValue.split(',')
        : [];

    const listeners = rawList
      .map((item) => String(item || '').trim())
      .filter((item) => !!item);

    return listeners.length ? listeners : undefined;
  }

  private extractLooseRequestParams(payload: any): Record<string, any> {
    if (!payload || typeof payload !== 'object') return {};

    const hasStructuredRequestParams =
      (payload.requestData && typeof payload.requestData === 'object') ||
      (payload.requestParams && typeof payload.requestParams === 'object');

    if (hasStructuredRequestParams) return {};

    const reservedKeys = new Set([
      'queryParams',
      'requestParams',
      'requestData',
      'filterListener',
      'listenerWidget',
      'listenerWidgets',
      'ListenerWidget',
      'ListenerWidgets',
      'listnerWidget',
      'listnerWidgets',
      'ListnerWidget',
      'ListnerWidgets',
      'Listnerwidget',
      'sourceWidgetId',
    ]);
    const looseParams: Record<string, any> = {};

    for (const [key, value] of Object.entries(payload)) {
      if (reservedKeys.has(key) || value === undefined || value === null || value === '') {
        continue;
      }
      looseParams[key] = value;
    }

    return looseParams;
  }

  private firstString(...values: unknown[]): string | null {
    for (const value of values) {
      if (typeof value === 'string' && value.trim()) {
        return value.trim();
      }
    }
    return null;
  }

  private normalizeDashboardNamePosition(value: unknown): DashboardNamePosition {
    return typeof value === 'string' && value.trim().toLowerCase() === 'top'
      ? 'top'
      : 'below';
  }

  private resolveDashboardTitle(config: any): string {
    return String(
      config?.['dashboard-name'] ??
      config?.dashboardName ??
      config?.dashboard_name ??
      ''
    ).trim();
  }

  private setDashboardTitleContext(config: any): void {
    this.dashboardTitleService.setDashboardTitle(
      this.resolveDashboardTitle(config),
      this.normalizeDashboardNamePosition(
        config?.dashboardNamePosition ??
        config?.['dashboardNamePosition'] ??
        config?.['dashboard-name-position']
      ),
    );
  }

  private normalizeDashboardNameFontSize(value: unknown): string {
    if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
      return `${value}rem`;
    }

    if (typeof value !== 'string') return '';

    const fontSize = value.trim();
    if (!fontSize || /\bpx\b/i.test(fontSize)) return '';

    return fontSize;
  }

  loadDashboardFromApi(projectId: string, appId: string) {
    const configSub = this.platformService
      .getDashboardConfigFromApi(projectId, appId)
      .pipe(
        switchMap((response: any) => {
          if (!response?.length) {
            return of({ config: null, persistedLayouts: null });
          }

          const config = response[0].dvm_workflow_map;
          // Priority: user's saved active theme (Save Theme button) > current
          // in-memory theme (covers a still-pending preview not yet saved) >
          // light/dark toggle > dashboard config > env default > 'default'.
          // Without the saved-name check, navigating away and back would
          // revert a saved theme to the dashboard JSON's `theme` field —
          // because `getUserPreference()` reads `dvm.theme` (light/dark
          // toggle), not `dvm.theme.activeName` (Save Theme target).
          const themeName =
            this.themeService.getSavedThemeName() ||
            this.themeService.currentThemeName ||
            this.themeService.getUserPreference() ||
            config?.theme || (environment as any).THEME_NAME || 'default';

          return this.themeService.loadTheme(themeName).pipe(
            tap((theme) => {
              this.themeService.initFromLoaded(themeName, theme);

              if (config?.themeOverrides) {
                this.themeService.setDashboardOverrides(config.themeOverrides);
              }
            }),
            switchMap(() => this.roleVisibilityService.loadMapping().pipe(
              switchMap(() =>
              this.loadPersistedLayouts(config).pipe(
                map((persistedLayouts) => ({ config, persistedLayouts }))
              )
            )
            ))
          );
        })
      )
      .subscribe({
        next: ({ config, persistedLayouts }: { config: any; persistedLayouts: Record<string, GridItem[]> | null }) => {
          if (!config) return;
          this.persistedLayouts = persistedLayouts;
          this.originalDashboardConfig = config;
          // Per-dashboard layout: tell the service which dashboard is active
          // (sourceKey = appId here) and what its JSON-declared default is,
          // so the resolved layout reflects this dashboard's saved pick or
          // its `layoutId` field. Must precede applyDashboardWithLayout —
          // the resulting activeLayoutId$ emission also fires the layout
          // subscription, but the signature check makes the second pass a
          // no-op.
          this.layoutService.setActiveDashboardKey(
            this.sourceKey,
            config?.layoutId ?? null,
          );
          this.applyDashboardWithLayout(config);
        },
        error: (err) =>
          console.error('Error loading dashboard config:', err),
      });

    this.subs.add(configSub); // ✅ fixed
  }

  loadCharts() {
    const configSub = this.configService
      .getDashboardConfig(this.configFileName)
      .pipe(
        switchMap((response: any) => {
          // Priority: user's saved active theme (Save Theme button) > current
          // in-memory theme (covers a still-pending preview not yet saved) >
          // light/dark toggle > dashboard config > env default > 'default'.
          const themeName =
            this.themeService.getSavedThemeName() ||
            this.themeService.currentThemeName ||
            this.themeService.getUserPreference() ||
            response?.theme || (environment as any).THEME_NAME || 'default';

          return this.themeService.loadTheme(themeName).pipe(
            tap((theme) => {
              this.themeService.initFromLoaded(themeName, theme);

              if (response?.themeOverrides) {
                this.themeService.setDashboardOverrides(
                  response.themeOverrides
                );
              }
            }),
            switchMap(() => this.roleVisibilityService.loadMapping().pipe(
              switchMap(() =>
              this.loadPersistedLayouts(response).pipe(
                map((persistedLayouts) => ({ response, persistedLayouts }))
              )
            )
            ))
          );
        })
      )
      .subscribe(({ response, persistedLayouts }) => {
        this.persistedLayouts = persistedLayouts;
        this.originalDashboardConfig = response;
        // Per-dashboard layout — see loadDashboardFromApi for the rationale.
        this.layoutService.setActiveDashboardKey(
          this.sourceKey,
          response?.layoutId ?? null,
        );
        this.applyDashboardWithLayout(response);
      });

    this.subs.add(configSub); //  fixed
  }

  /**
   * Resolve the effective dashboard config (reshape via active Custom Layout
   * if one is selected, else use the dashboard's own pageContent), then run
   * the height + screen-floating + grid + responsive pipeline.
   *
   * Skips the work if the resolved layout signature matches what's already
   * applied — avoids unnecessary chart re-renders on benign emissions
   * (BehaviorSubject seed, manifest-loaded callback firing identical state).
   */
  private applyDashboardWithLayout(config: any): void {
    const activeLayoutId = this.layoutService.getActiveLayoutId();
    const reshaped = this.layoutService.reshapePageContent(
      config?.pageContent,
      activeLayoutId,
    );
    const effective = this.roleVisibilityService.applyToDashboardConfig({ ...config, pageContent: reshaped });
    const signature = JSON.stringify(reshaped ?? null);
    this.setDashboardTitleContext(effective);
    if (signature === this.appliedLayoutSignature) return;
    this.appliedLayoutSignature = signature;

    //  HEIGHT SYSTEM
    this.configurations = this.applyWidgetHeights(effective);
    this.applyNavigationFiltersToGlobalService(this.configurations);

    //  SCREEN FLOATING
    this.applyScreenFloating(this.configurations);

    //  GRID ENGINE (CRITICAL)
    this.applyConfig();

    //  RESPONSIVE SERVICE
    this.responsiveLayoutService.setRowCount(
      this.configurations?.pageContent?.length || 0,
    );

    // ECharts doesn't observe its container — without an explicit resize()
    // signal after a layout/reshape, charts keep stale dimensions and
    // visibly misbehave. RAF lets the new grid styles flush before reflow.
    requestAnimationFrame(() => this.responsiveLayoutService.triggerReflow());
  }

  /**
   * Push navigation-carried values into GlobalFilterService using dashboard
   * filter metadata so charts receive them as request/query params on first load.
   */
  private applyNavigationFiltersToGlobalService(config: any): void {
    if (this.navFiltersApplied) return;

    const fromNav = this.buildInitialFiltersFromNavigation();
    const dashboardFilters = config?.filters?.content || [];
    console.log('[DVM NAV DEBUG] fromNav:', fromNav);
    console.log('[DVM NAV DEBUG] dashboard filter keys:', (dashboardFilters || []).map((f: any) => f?.key));

    const queryParams = { ...(fromNav.queryParams || {}) } as Record<string, any>;
    const hasNavigationListeners = !!fromNav.filterListener?.length;
    const navigationRequestParams = hasNavigationListeners
      ? { ...(fromNav.requestParams || {}) }
      : {};
    const requestParams = hasNavigationListeners
      ? {}
      : ({ ...(fromNav.requestParams || {}) } as Record<string, any>);

    if (dashboardFilters?.length) {
      for (const filter of dashboardFilters) {
        const key = String(filter?.key || '').trim();
        if (!key) continue;
        const raw = queryParams[key] ?? requestParams[key];
        if (raw === undefined || raw === null || raw === '') continue;

        const normalized = Array.isArray(raw)
          ? raw
          : (filter?.isMultiSelect ? [raw] : raw);

        if (filter?.isQueryParam) {
          queryParams[key] = normalized;
        } else {
          requestParams[key] = normalized;
        }
      }
    }

    const nextFilters = {
      queryParams,
      requestParams,
      widgetId: null,
      ...(hasNavigationListeners ? { navigationRequestParams } : {}),
      ...(hasNavigationListeners ? { navigationFilterListener: fromNav.filterListener } : {}),
      ...(fromNav.sourceWidgetId ? { navigationSourceWidgetId: fromNav.sourceWidgetId } : {}),
    };

    this.globalFilterService.updateAllFilters(nextFilters);
    console.log('[DVM NAV DEBUG] applied global filters:', {
      queryParams,
      requestParams,
      navigationRequestParams,
      filterListener: fromNav.filterListener,
    });
    this.navFiltersApplied = true;
  }

  /** Re-apply the active layout against the cached original config.
   *  No-op until the dashboard config has loaded. */
  private maybeReapplyLayout(): void {
    if (!this.originalDashboardConfig) return;
    this.applyDashboardWithLayout(this.originalDashboardConfig);
  }

  private applyScreenFloating(config: any): void {
    // Default to true (scrolling enabled) if key is not present
    this.screenFloating = config?.screenFloating !== false;
    this.responsiveLayoutService.setScreenFloating(this.screenFloating);
    if (this.screenFloating) {
      this.responsiveLayoutService.setCompactLayoutFitOverride(null);
      this.compactScrollLock = false;
      this.compactScrollLockSnapshot = null;
    }
  }

  private applyWidgetHeights(config: any): any {
    if (!config || !Array.isArray(config.pageContent)) {
      return config;
    }

    const updatedPageContent = config.pageContent.map((row: any) => ({
      ...row,
      columns: this.normalizeColumns(row?.columns || []),
    }));

    return {
      ...config,
      pageContent: updatedPageContent,
    };
  }

  private applyWidgetHeightsInPlace(config: any): void {
    if (!config || !Array.isArray(config.pageContent)) {
      return;
    }

    for (const row of config.pageContent) {
      this.applyColumnHeightsInPlace(row?.columns || []);
    }
  }

  private applyColumnHeightsInPlace(columns: any[]): void {
    for (const column of columns) {
      if (column?.type) {
        column.height = this.resolveWidgetHeight(column);
      }

      if (Array.isArray(column?.columns)) {
        this.applyColumnHeightsInPlace(column.columns);
      }

      if (Array.isArray(column?.rows)) {
        for (const nestedRow of column.rows) {
          if (nestedRow?.type) {
            nestedRow.height = this.resolveWidgetHeight(nestedRow);
          }

          if (Array.isArray(nestedRow?.columns)) {
            this.applyColumnHeightsInPlace(nestedRow.columns);
          }

          if (Array.isArray(nestedRow?.rows)) {
            this.applyColumnHeightsInPlace(nestedRow.rows);
          }
        }
      }
    }
  }

  private normalizeColumns(columns: any[]): any[] {
    return columns.map((column: any) => {
      const updatedColumn = { ...column };

      if (updatedColumn?.type) {
        updatedColumn.height = this.resolveWidgetHeight(updatedColumn);
      }

      if (Array.isArray(updatedColumn?.columns)) {
        updatedColumn.columns = this.normalizeColumns(updatedColumn.columns);
      }

      if (Array.isArray(updatedColumn?.rows)) {
        updatedColumn.rows = updatedColumn.rows.map((nestedRow: any) => ({
          ...nestedRow,
          columns: this.normalizeColumns(nestedRow?.columns || []),
          ...(nestedRow?.type
            ? { height: this.resolveWidgetHeight(nestedRow) }
            : {}),
        }));
      }

      return updatedColumn;
    });
  }

  private resolveWidgetHeight(widget: any): string {
    return this.responsiveLayoutService.resolveWidgetHeight(widget);
  }



  @HostListener('window:resize')
  onViewportChange(): void {
    this.containerWidth = this.gridContainerRef?.nativeElement?.clientWidth || window.innerWidth;
    this.currentBp = this.resolveBp(window.innerWidth);
    if (!this.layouts[this.currentBp]?.length) this.layouts[this.currentBp] = this.buildDefaultLayout(this.currentBp);
    this.updateRowsForResponsiveService();
    this.releaseCompactScrollLockIfViewportImproved();
    this.queueCompactFitMeasurement();
  }

  @HostListener('window:dvm-widget-height-override', ['$event'])
  onWidgetHeightOverride(event: CustomEvent<{ widgetId?: string; heightPx?: number; restore?: boolean }>): void {
    const widgetId = String(event?.detail?.widgetId || '').trim();
    if (!widgetId) return;

    const layout = this.layouts[this.currentBp] ?? [];
    const item = layout.find((entry) => entry.id === widgetId);
    if (!item) return;

    const widget = this.widgetMap.get(widgetId)?.widget;
    const resolvedRows = event?.detail?.restore
      ? this.heightToRows(this.resolveWidgetHeightForBreakpoint(widget, this.currentBp))
      : this.heightToRows(`${Math.ceil(Number(event?.detail?.heightPx || 0))}px`);
    const nextRows = this.clamp(resolvedRows, item.minH, item.maxH);

    if (!Number.isFinite(nextRows) || nextRows === item.h) return;
    this.updateItem(widgetId, { h: nextRows });
  }

  @HostListener('window:pointermove', ['$event'])
  onPointerMove(event: PointerEvent): void {
    if (!this.interaction || event.pointerId !== this.interaction.pointerId) return;
    event.preventDefault();
    this.queued = { x: event.clientX, y: event.clientY };
    if (this.raf) return;
    this.raf = true;
    requestAnimationFrame(() => {
      this.raf = false;
      if (!this.interaction || !this.queued) return;
      this.applyInteraction(this.queued.x, this.queued.y);
      this.queued = null;
    });
  }

  @HostListener('window:pointerup', ['$event'])
  @HostListener('window:pointercancel', ['$event'])
  onPointerUp(event: PointerEvent): void {
    if (!this.interaction || event.pointerId !== this.interaction.pointerId) return;
    this.finishInteraction(false);
  }

  @HostListener('window:blur')
  onBlur(): void {
    if (this.interaction) this.finishInteraction(false);
  }

  @HostListener('window:keydown.escape')
  onEscape(): void {
    void this.exitFocusMode();
  }

  @HostListener('document:fullscreenchange')
  onFullscreenChange(): void {
    const prevFocusedId = this.focusedWidgetId;
    const active = document.fullscreenElement as HTMLElement | null;
    const nextId = active?.getAttribute('data-widget-id') || null;
    if (active) {
      this.syncChartTooltipContainer(active, true);
      this.startFocusTooltipSync(active);
    } else {
      this.stopFocusTooltipSync();
    }
    if (!nextId && prevFocusedId) {
      const prevEl = this.getWidgetElement(prevFocusedId);
      if (prevEl) this.syncChartTooltipContainer(prevEl, false);
    }
    this.setFocusedWidget(nextId);
  }

  widgetStyle(item: GridItem): Record<string, string> {
    return { 'grid-column': `${item.x + 1} / span ${item.w}`, 'grid-row': `${item.y + 1} / span ${item.h}` };
  }

  label(widget: any): string {
    return String(widget?.title || widget?.widgetId || 'widget');
  }

  focusToggleIcon(id: string): string {
    return this.isFocused(id) ? 'close_fullscreen' : 'open_in_full';
  }

  focusToggleLabel(id: string, widget: any): string {
    const action = this.isFocused(id) ? 'Close focus for ' : 'Open focus for ';
    return `${action}${this.label(widget)}`;
  }

  isFocused(id: string): boolean {
    return this.focusedWidgetId === id;
  }

  canShowFocusToggle(widget: any): boolean {
    if (!this.focusMode.enabled) return false;
    const type = this.normalizeType(widget?.type);
    if (!type) return false;
    if (this.focusMode.excludeTypes.has(type)) return false;
    if (this.focusMode.includeTypes.size) return this.focusMode.includeTypes.has(type);
    return !NON_FOCUS_WIDGET_TYPES.has(type);
  }

  onFocusTogglePointerDown(event: PointerEvent): void {
    event.stopPropagation();
  }

  toggleFocus(id: string): void {
    if (this.focusedWidgetId === id) {
      void this.exitFocusMode();
      return;
    }
    void this.enterFocusMode(id);
  }

  canDrag(item: GridItem): boolean { return this.editMode && !this.hasFocusWidget && this.grid.draggable && !item.locked; }
  canResize(item: GridItem): boolean { return this.editMode && !this.hasFocusWidget && this.grid.resizable && !item.locked; }
  isDragging(id: string): boolean { return this.activeDragId === id; }
  isResizing(id: string): boolean { return this.activeResizeId === id; }

  enterEditMode(): void {
    if (!this.layoutEditEnabled) return;
    if (this.editMode) return;

    this.editSessionLayouts = this.cloneLayouts(this.layouts);
    this.editMode = true;
  }

  cancelEditMode(): void {
    if (!this.editMode) return;

    if (this.interaction) {
      this.finishInteraction(false);
    }

    const resetLayouts = this.cloneLayouts(this.editSessionLayouts);
    if (resetLayouts) {
      this.layouts = resetLayouts;
      this.syncDashboardAfterLayoutChange();
    }

    this.exitEditMode();
  }

  saveMyLayout(): void {
    this.saveLayout('USER');
  }

  saveDefaultLayout(): void {
    if (!this.canSaveDefaultLayout) return;
    this.saveLayout('GLOBAL');
  }

  startDrag(event: PointerEvent, item: GridItem): void { this.startInteraction(event, item, 'drag'); }
  startResize(event: PointerEvent, item: GridItem, corner: 'nw' | 'ne' | 'sw' | 'se'): void {
    this.startInteraction(event, item, 'resize', corner);
  }
  onWidgetPointerDown(event: PointerEvent, item: GridItem): void {
    if (!this.editMode || !this.canDrag(item)) return;
    const target = event.target;
    if (!(target instanceof Element)) return;
    if (target.closest('.resize-handle') || target.closest('.focus-mode-toggle')) return;
    const host = event.currentTarget as HTMLElement | null;
    const hasHeader = !!host?.querySelector('.card-header');
    // If widget has a card header, drag starts from header only.
    if (hasHeader && !target.closest('.card-header')) return;
    this.startDrag(event, item);
  }

  private applyConfig(): void {
    this.responsiveLayoutService.setScreenFloating(this.configurations?.screenFloating !== false);
    this.widgets = this.extractWidgets(this.configurations?.pageContent ?? []);
    this.widgetMap = new Map(this.widgets.map((w) => [w.id, w]));
    this.hiddenWidgetIds = new Set(
      this.widgets
        .filter((rec) => rec.widget?.initiallyHidden === true || rec.widget?.visible === false)
        .map((rec) => rec.id),
    );
    this.resolveFocusModeConfig();
    if (this.focusedWidgetId) {
      const focusedRec = this.widgetMap.get(this.focusedWidgetId);
      if (!focusedRec || !this.canShowFocusToggle(focusedRec.widget)) void this.exitFocusMode();
    }
    this.resolveGridConfig();
    this.resolveLayoutEditConfig();
    this.layouts = {};
    const persisted = this.readPersisted();
    for (const bp of this.orderedBps) {
      const base = this.buildDefaultLayout(bp);
      const raw = this.parseRawLayout(this.grid.rawLayouts[bp] ?? [], bp);
      const mergedA = raw.length ? this.merge(base, raw, bp) : base;
      const mergedB = persisted?.[bp]?.length ? this.merge(mergedA, persisted[bp], bp) : mergedA;
      this.layouts[bp] = this.pack(mergedB, null, this.grid.columns[bp] ?? 12);
    }
    this.onViewportChange();
    this.queueCompactFitMeasurement();
  }

  private resolveGridConfig(): void {
    const c = this.configurations?.layoutConfig ?? this.configurations?.gridLayout ?? {};
    const defaultWidgetHeight =
      String(
        this.configurations?.defaultWidgetHeight ??
        this.configurations?.defaultHeight ??
        this.configurations?.defaultheight ??
        c?.defaultWidgetHeight ??
        c?.defaultheight ??
        ''
      ).trim();
    if (defaultWidgetHeight) {
      this.responsiveLayoutService.setDefaultWidgetHeight(defaultWidgetHeight);
    }
    this.grid.breakpoints = this.mapNumbers(c?.breakpoints, BPS);
    this.grid.columns = this.mapNumbers(c?.columns ?? c?.cols, COLS);
    this.grid.rowHeight = this.num(c?.rowHeight, 72);
    this.grid.gap = this.num(Array.isArray(c?.margin) ? c.margin[0] : (c?.gap ?? c?.margin), 12);
    this.grid.draggable = c?.draggable !== false;
    this.grid.resizable = c?.resizable !== false;
    this.grid.persist = c?.persist !== false;
    this.grid.persistKey = String(c?.persistKey ?? '').trim();
    this.grid.defaultW = this.num(c?.defaultWidth, 4);
    this.grid.defaultH = this.num(c?.defaultHeight, 4);
    this.grid.defaultMinW = this.num(c?.defaultMinW, 2);
    this.grid.defaultMinH = this.num(c?.defaultMinH, 2);
    this.grid.defaultMaxH = this.num(c?.defaultMaxH, 16);
    this.grid.rawLayouts = typeof c?.layouts === 'object' && c?.layouts ? c.layouts : {};
  }

  private resolveLayoutEditConfig(): void {
    const c = this.configurations?.layoutConfig ?? this.configurations?.gridLayout ?? {};
    const enabled = this.firstEnabledFlag(
      this.configurations?.enableEdit,
      this.configurations?.editable,
      this.configurations?.editMode,
      this.configurations?.layoutEdit,
      this.configurations?.layoutEditMode,
      c?.enableEdit,
      c?.editable,
      c?.edit,
      c?.editMode,
      c?.layoutEdit
    );

    this.layoutEditEnabled = enabled === true;

    if (!this.layoutEditEnabled && this.editMode) {
      if (this.interaction) this.finishInteraction(false);
      this.editMode = false;
      this.activeDragId = null;
      this.activeResizeId = null;
    }
  }

  private resolveFocusModeConfig(): void {
    const dashboardConfig = this.configurations?.focusMode
      ?? this.configurations?.enableFocusMode
      ?? this.configurations?.focus;
    const gridConfig = this.configurations?.layoutConfig?.focusMode
      ?? this.configurations?.layoutConfig?.enableFocusMode
      ?? this.configurations?.gridLayout?.focusMode
      ?? this.configurations?.gridLayout?.enableFocusMode;
    const raw = dashboardConfig ?? gridConfig;

    if (typeof raw === 'boolean') {
      this.focusMode = { enabled: raw, includeTypes: new Set<string>(), excludeTypes: new Set<string>() };
      if (!raw) void this.exitFocusMode();
      return;
    }

    if (!raw || typeof raw !== 'object') {
      this.focusMode = { enabled: false, includeTypes: new Set<string>(), excludeTypes: new Set<string>() };
      void this.exitFocusMode();
      return;
    }

    const cfg = raw as Record<string, unknown>;
    this.focusMode = {
      enabled: this.toEnabledFlag(cfg) === true,
      includeTypes: new Set<string>(this.mapStrings(cfg['includeTypes'] ?? cfg['types'] ?? cfg['chartTypes'])),
      excludeTypes: new Set<string>(this.mapStrings(cfg['excludeTypes'] ?? cfg['disabledTypes'])),
    };

    if (!this.focusMode.enabled) void this.exitFocusMode();
  }

  private async enterFocusMode(id: string): Promise<void> {
    const rec = this.widgetMap.get(id);
    if (!rec || !this.canShowFocusToggle(rec.widget)) return;

    const target = this.getWidgetElement(id);
    if (!target?.requestFullscreen) return;

    if (this.interaction) this.finishInteraction(false);
    try {
      if (document.fullscreenElement && document.fullscreenElement !== target) {
        const active = document.fullscreenElement as HTMLElement | null;
        if (active) this.syncChartTooltipContainer(active, false);
        this.stopFocusTooltipSync();
        await document.exitFullscreen();
      }
      await target.requestFullscreen();
      this.syncChartTooltipContainer(target, true);
      this.startFocusTooltipSync(target);
      this.setFocusedWidget(id);
    } catch (error) {
      console.error('Unable to enter focus mode fullscreen:', error);
      this.syncChartTooltipContainer(target, false);
      this.stopFocusTooltipSync();
      this.setFocusedWidget(null);
    }
  }

  private async exitFocusMode(): Promise<void> {
    this.stopFocusTooltipSync();
    const active = document.fullscreenElement as HTMLElement | null;
    if (active) this.syncChartTooltipContainer(active, false);
    try {
      if (document.fullscreenElement) await document.exitFullscreen();
    } catch (error) {
      console.error('Unable to exit focus mode fullscreen:', error);
    } finally {
      this.setFocusedWidget(null);
    }
  }

  private setFocusedWidget(id: string | null): void {
    let nextId = id;
    if (nextId) {
      const rec = this.widgetMap.get(nextId);
      if (!rec || !this.canShowFocusToggle(rec.widget)) nextId = null;
    }
    if (this.focusedWidgetId === nextId) return;

    if (this.interaction) this.finishInteraction(false);
    this.focusedWidgetId = nextId;
    this.syncFocusBodyClass();
    requestAnimationFrame(() => this.responsiveLayoutService.triggerReflow());
  }

  private getWidgetElement(id: string): HTMLElement | null {
    return this.gridContainerRef?.nativeElement
      ?.querySelector(`.grid-widget[data-widget-id="${id}"]`) as HTMLElement | null;
  }

  private syncChartTooltipContainer(widgetEl: HTMLElement, fullscreen: boolean, triggerReflow = true): void {
    const chartDoms = this.collectChartDoms(widgetEl);
    chartDoms.forEach((chartHost) => {
      const chart = echarts.getInstanceByDom(chartHost);
      if (!chart) return;

      const option = chart.getOption() as any;
      const tooltipList = Array.isArray(option?.tooltip)
        ? option.tooltip
        : (option?.tooltip ? [option.tooltip] : []);
      if (!tooltipList.length) return;

      if (fullscreen) {
        if (!this.tooltipAppendSnapshot.has(chartHost)) {
          this.tooltipAppendSnapshot.set(
            chartHost,
            tooltipList.map((t: any) => ({
              appendToBody: (t && typeof t === 'object' && typeof t.appendToBody === 'boolean') ? t.appendToBody : undefined,
              appendTo: (t && typeof t === 'object') ? t.appendTo : undefined,
            }))
          );
        }

        const patch = tooltipList.map((t: any) => ({
          ...(t && typeof t === 'object' ? t : {}),
          appendToBody: false,
          appendTo: widgetEl,
        }));
        chart.setOption({ tooltip: patch.length === 1 ? patch[0] : patch }, { lazyUpdate: true });
        return;
      }

      const snapshot = this.tooltipAppendSnapshot.get(chartHost);
      if (!snapshot?.length) return;
      const patch = tooltipList.map((t: any, idx: number) => {
        const snap = snapshot[idx];
        return {
          ...(t && typeof t === 'object' ? t : {}),
          appendToBody: snap?.appendToBody === true,
          appendTo: snap && 'appendTo' in snap ? snap.appendTo : null,
        };
      });
      chart.setOption({ tooltip: patch.length === 1 ? patch[0] : patch }, { lazyUpdate: true });
      this.tooltipAppendSnapshot.delete(chartHost);
    });

    if (triggerReflow) {
      requestAnimationFrame(() => this.responsiveLayoutService.triggerReflow());
    }
  }

  private collectChartDoms(widgetEl: HTMLElement): HTMLElement[] {
    const found = new Set<HTMLElement>();
    const els = widgetEl.querySelectorAll('*');
    els.forEach((n) => {
      const chart = echarts.getInstanceByDom(n as HTMLElement);
      if (chart) {
        const dom = chart.getDom() as HTMLElement;
        if (dom) found.add(dom);
      }
    });
    return [...found];
  }

  private startFocusTooltipSync(widgetEl: HTMLElement): void {
    this.stopFocusTooltipSync();
    this.focusTooltipSyncTimer = setInterval(() => {
      const active = document.fullscreenElement as HTMLElement | null;
      if (!active || active !== widgetEl) {
        this.stopFocusTooltipSync();
        return;
      }
      this.syncChartTooltipContainer(widgetEl, true, false);
    }, 300);
  }

  private stopFocusTooltipSync(): void {
    if (!this.focusTooltipSyncTimer) return;
    clearInterval(this.focusTooltipSyncTimer);
    this.focusTooltipSyncTimer = undefined;
  }

  private syncFocusBodyClass(): void {
    if (typeof document === 'undefined') return;
    document.body.classList.toggle('dashboard-focus-active', !!this.focusedWidgetId);
  }

  private startInteraction(
    event: PointerEvent,
    item: GridItem,
    mode: 'drag' | 'resize',
    corner?: 'nw' | 'ne' | 'sw' | 'se'
  ): void {
    if (event.button !== 0 || (mode === 'drag' && !this.canDrag(item)) || (mode === 'resize' && !this.canResize(item))) return;
    event.preventDefault(); event.stopPropagation();
    this.interaction = {
      mode, id: item.id, corner, pointerId: event.pointerId, startClientX: event.clientX, startClientY: event.clientY,
      startX: item.x, startY: item.y, startW: item.w, startH: item.h,
    };
    this.activeDragId = mode === 'drag' ? item.id : null;
    this.activeResizeId = mode === 'resize' ? item.id : null;
    const target = event.currentTarget as HTMLElement | null;
    this.interactionTarget = target ?? undefined;
    target?.setPointerCapture?.(event.pointerId);
  }

  private applyInteraction(clientX: number, clientY: number): void {
    if (!this.interaction) return;
    const item = (this.layouts[this.currentBp] ?? []).find((x) => x.id === this.interaction?.id);
    if (!item) return;
    const dx = Math.round((clientX - this.interaction.startClientX) / (this.cellW() + this.grid.gap));
    const dy = Math.round((clientY - this.interaction.startClientY) / (this.grid.rowHeight + this.grid.gap));
    if (this.interaction.mode === 'drag') {
      this.updateItem(item.id, { x: this.clamp(this.interaction.startX + dx, 0, this.cols - item.w), y: Math.max(0, this.interaction.startY + dy) });
    } else {
      const i = this.interaction;
      const corner = i.corner ?? 'se';
      let x = i.startX;
      let y = i.startY;
      let w = i.startW;
      let h = i.startH;

      if (corner === 'se') {
        const maxW = Math.min(item.maxW, this.cols - i.startX);
        w = this.clamp(i.startW + dx, item.minW, maxW);
        h = this.clamp(i.startH + dy, item.minH, item.maxH);
      } else if (corner === 'sw') {
        const maxW = Math.min(item.maxW, i.startX + i.startW);
        w = this.clamp(i.startW - dx, item.minW, maxW);
        x = i.startX + (i.startW - w);
        h = this.clamp(i.startH + dy, item.minH, item.maxH);
      } else if (corner === 'ne') {
        const maxW = Math.min(item.maxW, this.cols - i.startX);
        const maxH = Math.min(item.maxH, i.startY + i.startH);
        w = this.clamp(i.startW + dx, item.minW, maxW);
        h = this.clamp(i.startH - dy, item.minH, maxH);
        y = i.startY + (i.startH - h);
      } else {
        const maxW = Math.min(item.maxW, i.startX + i.startW);
        const maxH = Math.min(item.maxH, i.startY + i.startH);
        w = this.clamp(i.startW - dx, item.minW, maxW);
        h = this.clamp(i.startH - dy, item.minH, maxH);
        x = i.startX + (i.startW - w);
        y = i.startY + (i.startH - h);
      }

      this.updateItem(item.id, { x, y, w, h });
    }
  }

  private finishInteraction(persist: boolean): void {
    if (!this.interaction) return;
    try { this.interactionTarget?.releasePointerCapture?.(this.interaction.pointerId); } catch { }
    this.interaction = undefined; this.interactionTarget = undefined; this.activeDragId = null; this.activeResizeId = null; this.queued = null; this.raf = false;
    if (persist) this.writePersisted();
  }

  private updateItem(id: string, changes: Partial<GridItem>): void {
    const cur = this.layouts[this.currentBp] ?? [];
    const idx = cur.findIndex((x) => x.id === id);
    if (idx < 0) return;
    const next = cur.map((x) => ({ ...x }));
    next[idx] = this.sanitize({ ...next[idx], ...changes }, this.cols);
    this.layouts[this.currentBp] = this.pack(next, id, this.cols);
    this.updateRowsForResponsiveService();
  }

  @HostListener('window:pat-price-planner-open', ['$event'])
  onPricePlannerOpen(event: CustomEvent): void {
    const detail = event?.detail || {};
    const chartWidgetId = String(detail.chartWidgetId || 'widget-grouped-bar-chart-with-status-table');
    const plannerWidgetId = String(detail.plannerWidgetId || 'widget-price-planner');
    const chartWidth = Number(detail.chartWidth || 8);
    const plannerWidth = Number(detail.plannerWidth || 4);
    const plannerRequestParams = this.resolvePricePlannerOpenRequestParams(detail);

    this.applyPricePlannerOpenRequest(chartWidgetId, plannerWidgetId, plannerRequestParams);

    this.hiddenWidgetIds.delete(plannerWidgetId);
    this.updateSplitPanelLayout(chartWidgetId, plannerWidgetId, chartWidth, plannerWidth);
  }

  private applyPricePlannerOpenRequest(
    chartWidgetId: string,
    plannerWidgetId: string,
    requestParams: Record<string, any[]>,
  ): void {
    if (!requestParams || !Object.keys(requestParams).length) {
      return;
    }

    const currentFilters = this.globalFilterService.getCurrentFilters?.() || {};
    this.globalFilterService.updateAllFilters({
      ...currentFilters,
      widgetId: chartWidgetId,
      navigationRequestParams: requestParams,
      navigationFilterListener: [plannerWidgetId],
      navigationSourceWidgetId: chartWidgetId,
      filterListener: [plannerWidgetId],
    });

    const rec = this.widgetMap.get(plannerWidgetId);
    if (!rec) {
      return;
    }

    const applyRequestParams = this.resolvePricePlannerApplyRequestParams(requestParams, rec.widget);
    const nextWidget = {
      ...(rec.widget || {}),
      defaultRequestData: applyRequestParams,
      actionButton: {
        ...(rec.widget?.actionButton || {}),
        includeNavigationRequestParamsOnSubmit: false,
        submitRequestKeys: Object.keys(applyRequestParams),
      },
    };
    rec.widget = nextWidget;
    this.widgets = this.widgets.map((item) => item.id === plannerWidgetId ? { ...item, widget: nextWidget } : item);
    this.widgetMap = new Map(this.widgets.map((item) => [item.id, item]));
  }

  private resolvePricePlannerOpenRequestParams(detail: any): Record<string, any[]> {
    const directRequestParams = this.normalizePlannerRequestParams(detail?.requestParams);
    if (Object.keys(directRequestParams).length) {
      return directRequestParams;
    }

    const sourceRow = detail?.sourceRow;
    if (!sourceRow || typeof sourceRow !== 'object') {
      return {};
    }

    const fieldMap = detail?.requestFieldMap && typeof detail.requestFieldMap === 'object'
      ? detail.requestFieldMap
      : {
          customer_name: 'customer_name',
          equipment: 'equipment',
          engine: 'engine',
          emission: 'emission',
        };
    const requestParams: Record<string, any[]> = {};

    Object.keys(fieldMap).forEach((targetKey) => {
      const mappedPath = String(fieldMap[targetKey] || targetKey);
      const values = this.toPlannerRequestArray(this.resolveObjectPath(sourceRow, mappedPath));
      if (values.length) {
        requestParams[targetKey] = values;
      }
    });

    return requestParams;
  }

  private resolvePricePlannerApplyRequestParams(
    requestParams: Record<string, any[]>,
    plannerWidget: any,
  ): Record<string, any[]> {
    const rawKeys = plannerWidget?.applyRequestKeys || plannerWidget?.chartConfig?.plannerPanel?.applyRequestKeys;
    const keys = Array.isArray(rawKeys) && rawKeys.length
      ? rawKeys.map((key: unknown) => String(key || '').trim()).filter(Boolean)
      : ['customer_name', 'equipment'];

    return keys.reduce<Record<string, any[]>>((acc, key) => {
      const values = this.toPlannerRequestArray(requestParams[key]);
      if (values.length) {
        acc[key] = values;
      }
      return acc;
    }, {});
  }

  private normalizePlannerRequestParams(value: any): Record<string, any[]> {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
      return {};
    }

    return Object.keys(value).reduce<Record<string, any[]>>((acc, key) => {
      const values = this.toPlannerRequestArray(value[key]);
      if (values.length) {
        acc[key] = values;
      }
      return acc;
    }, {});
  }

  private toPlannerRequestArray(value: any): any[] {
    if (Array.isArray(value)) {
      return value
        .flat(Infinity)
        .filter((item: any) => item !== undefined && item !== null && String(item).trim() !== '');
    }

    if (value === undefined || value === null || String(value).trim() === '') {
      return [];
    }

    return [value];
  }

  private resolveObjectPath(source: any, path: string): any {
    if (!source || !path) {
      return undefined;
    }

    return String(path)
      .split('.')
      .reduce((current: any, key: string) => current?.[key], source);
  }

  @HostListener('window:pat-price-planner-close', ['$event'])
  onPricePlannerClose(event: CustomEvent): void {
    const detail = event?.detail || {};
    const chartWidgetId = String(detail.chartWidgetId || 'widget-grouped-bar-chart-with-status-table');
    const plannerWidgetId = String(detail.plannerWidgetId || 'widget-price-planner');

    this.hiddenWidgetIds.add(plannerWidgetId);
    this.updateFullWidthChartLayout(chartWidgetId);
  }

  @HostListener('window:pat-price-planner-applied', ['$event'])
  onPricePlannerApplied(event: CustomEvent): void {
    const detail = event?.detail || {};
    const listenerWidgets = Array.isArray(detail.listenerWidgets) && detail.listenerWidgets.length
      ? detail.listenerWidgets.map((item: unknown) => String(item)).filter(Boolean)
      : [
          'widget-grouped-bar-chart-with-status-table',
          'widget-number-stats-n-pat-summary',
        ];
    const currentFilters = this.globalFilterService.getCurrentFilters?.() || {};
    const plannerWidgetId = String(detail.sourceWidgetId || 'widget-price-planner');
    const plannerWidget = this.widgetMap.get(plannerWidgetId)?.widget;
    const appliedRequestParams = this.resolvePricePlannerApplyRequestParams(
      this.normalizePlannerRequestParams(currentFilters.navigationRequestParams || {}),
      plannerWidget,
    );
    const refreshRequestParams = this.removePlannerDetailRequestParams({
      ...(currentFilters.requestParams || {}),
      ...appliedRequestParams,
    });

    this.globalFilterService.updateAllFilters({
      ...currentFilters,
      widgetId: plannerWidgetId,
      filterListener: listenerWidgets,
      requestParams: refreshRequestParams,
    });
  }

  private removePlannerDetailRequestParams(requestParams: Record<string, any>): Record<string, any> {
    const next = { ...(requestParams || {}) };
    delete next['equipment'];
    delete next['engine'];
    delete next['emission'];
    return next;
  }

  private updateSplitPanelLayout(
    chartWidgetId: string,
    plannerWidgetId: string,
    chartWidth: number,
    plannerWidth: number,
  ): void {
    const cur = this.layouts[this.currentBp] ?? [];
    const chart = cur.find((item) => item.id === chartWidgetId);
    const planner = cur.find((item) => item.id === plannerWidgetId);
    if (!chart || !planner) return;

    const cols = this.cols;
    const safePlannerWidth = this.clamp(plannerWidth, 1, cols);
    const safeChartWidth = this.clamp(chartWidth, 1, cols - safePlannerWidth);
    const next = cur.map((item) => {
      if (item.id === chartWidgetId) {
        return this.sanitize({ ...item, x: 0, w: safeChartWidth }, cols);
      }
      if (item.id === plannerWidgetId) {
        return this.sanitize({
          ...item,
          x: safeChartWidth,
          y: chart.y,
          w: safePlannerWidth,
          h: chart.h,
        }, cols);
      }
      return { ...item };
    });

    this.layouts[this.currentBp] = this.pack(next, plannerWidgetId, cols);
    this.updateRowsForResponsiveService();
    requestAnimationFrame(() => this.responsiveLayoutService.triggerReflow());
  }

  private updateFullWidthChartLayout(chartWidgetId: string): void {
    const cur = this.layouts[this.currentBp] ?? [];
    const chart = cur.find((item) => item.id === chartWidgetId);
    if (!chart) return;

    const next = cur.map((item) => item.id === chartWidgetId
      ? this.sanitize({ ...item, x: 0, w: this.cols }, this.cols)
      : { ...item });

    this.layouts[this.currentBp] = this.pack(next, chartWidgetId, this.cols);
    this.updateRowsForResponsiveService();
    requestAnimationFrame(() => this.responsiveLayoutService.triggerReflow());
  }

  private buildDefaultLayout(bp: string): GridItem[] {
    const cols = this.grid.columns[bp] ?? 12;
    const raw = this.widgets.map((rec) => {
      const w = this.clamp(this.spanToCols(this.span(rec.widget?.class, bp), cols), 1, cols);
      const minW = this.clamp(this.num(rec.widget?.minW, this.grid.defaultMinW), 1, cols);
      const minH = this.clamp(this.num(rec.widget?.minH, this.grid.defaultMinH), 1, this.grid.defaultMaxH);
      const h = this.clamp(
        this.heightToRows(this.resolveWidgetHeightForBreakpoint(rec.widget, bp)),
        minH,
        this.grid.defaultMaxH
      );
      return { id: rec.id, x: 0, y: 0, w: this.clamp(w, minW, cols), h, minW, maxW: this.clamp(this.num(rec.widget?.maxW, cols), minW, cols), minH, maxH: this.clamp(this.num(rec.widget?.maxH, this.grid.defaultMaxH), minH, this.grid.defaultMaxH), locked: !!rec.widget?.locked };
    });
    let cursorX = 0, cursorY = 0, rowH = 0;
    for (const item of raw) {
      if (cursorX + item.w > cols) { cursorX = 0; cursorY += rowH; rowH = 0; }
      item.x = cursorX; item.y = cursorY;
      cursorX += item.w; rowH = Math.max(rowH, item.h);
    }
    return this.pack(raw, null, cols);
  }

  private parseRawLayout(raw: Array<Record<string, unknown>>, bp: string): GridItem[] {
    const cols = this.grid.columns[bp] ?? 12;
    return raw.map((r) => {
      const id = String(r['id'] ?? r['i'] ?? '');
      const wrec = this.widgetMap.get(id)?.widget;
      const minW = this.clamp(this.num(r['minW'] ?? wrec?.minW, this.grid.defaultMinW), 1, cols);
      const minH = this.clamp(this.num(r['minH'] ?? wrec?.minH, this.grid.defaultMinH), 1, this.grid.defaultMaxH);
      const maxW = this.clamp(this.num(r['maxW'] ?? wrec?.maxW, cols), minW, cols);
      const maxH = this.clamp(this.num(r['maxH'] ?? wrec?.maxH, this.grid.defaultMaxH), minH, this.grid.defaultMaxH);
      return this.sanitize({ id, x: this.num(r['x'], 0), y: this.num(r['y'], 0), w: this.num(r['w'], this.grid.defaultW), h: this.num(r['h'], this.grid.defaultH), minW, maxW, minH, maxH, locked: !!(r['locked'] ?? wrec?.locked) }, cols);
    }).filter((x) => !!x.id && this.widgetMap.has(x.id));
  }

  private merge(
    base: GridItem[],
    overlay: GridItem[],
    bp: string,
    options?: { preserveBaseHeight?: boolean }
  ): GridItem[] {
    const cols = this.grid.columns[bp] ?? 12;
    const map = new Map(overlay.map((x) => [x.id, x]));
    return base.map((b) => {
      const merged = map.has(b.id) ? { ...b, ...map.get(b.id)! } : { ...b };
      if (options?.preserveBaseHeight) {
        merged.h = b.h;
      }
      return this.sanitize(merged, cols);
    });
  }

  private pack(items: GridItem[], activeId: string | null, cols: number): GridItem[] {
    const order = [...items].sort((a, b) => {
      if (activeId) { if (a.id === activeId && b.id !== activeId) return -1; if (b.id === activeId && a.id !== activeId) return 1; }
      return a.y - b.y || a.x - b.x;
    });
    const placed: GridItem[] = [];
    for (const it of order) { const c = this.sanitize(it, cols); while (this.collides(c, placed)) c.y += 1; placed.push(c); }
    const compacted: GridItem[] = [];
    for (const it of placed.sort((a, b) => a.y - b.y || a.x - b.x)) {
      const c = this.sanitize({ ...it }, cols);
      if (it.id !== activeId) while (c.y > 0 && !this.collides({ ...c, y: c.y - 1 }, compacted)) c.y -= 1;
      while (this.collides(c, compacted)) c.y += 1;
      compacted.push(c);
    }
    const map = new Map(compacted.map((x) => [x.id, x]));
    return this.widgets.map((w) => map.get(w.id)).filter((x): x is GridItem => !!x);
  }

  private collides(a: GridItem, list: GridItem[]): boolean {
    return list.some((b) => a.id !== b.id && a.x < b.x + b.w && a.x + a.w > b.x && a.y < b.y + b.h && a.y + a.h > b.y);
  }

  private sanitize(it: GridItem, cols: number): GridItem {
    const minW = this.clamp(it.minW, 1, cols), maxW = this.clamp(it.maxW, minW, cols);
    const minH = this.clamp(it.minH, 1, this.grid.defaultMaxH), maxH = this.clamp(it.maxH, minH, this.grid.defaultMaxH);
    const w = this.clamp(it.w, minW, maxW), h = this.clamp(it.h, minH, maxH);
    return { ...it, x: this.clamp(it.x, 0, cols - w), y: Math.max(0, Math.floor(it.y)), w, h, minW, maxW, minH, maxH, locked: !!it.locked };
  }

  private extractWidgets(content: any[]): WidgetRec[] {
    const out: WidgetRec[] = []; const count = new Map<string, number>();
    const visit = (n: any): void => {
      if (!n || typeof n !== 'object') return;
      if (n.type) {
        const base = String(n.widgetId ?? n.id ?? `widget-${out.length + 1}`).replace(/[^a-zA-Z0-9-_]/g, '-');
        const idx = (count.get(base) ?? 0) + 1; count.set(base, idx);
        out.push({ id: idx === 1 ? base : `${base}__${idx}`, widget: n });
        // A typed node is a widget config. Its own `columns`/`rows` can be
        // component-specific config, such as super-table columns, not dashboard
        // layout children. Stop here to avoid rendering those configs as blank
        // dashboard widgets.
        return;
      }
      if (Array.isArray(n.columns)) n.columns.forEach((x: any) => visit(x));
      if (Array.isArray(n.rows)) n.rows.forEach((x: any) => visit(x));
      if (Array.isArray(n.stats)) n.stats.forEach((x: any) => visit(x));
    };
    content.forEach((x) => visit(x));
    return out;
  }

  private readPersisted(): Record<string, GridItem[]> | null {
    if (!this.grid.persist) return null;
    // When a custom layout is active, the layout's row pattern is the source
    // of truth. Drag positions saved against a previous layout (or no layout)
    // would override the new column widths and leave cycled rows misaligned.
    if (this.layoutService.getActiveLayoutId()) return null;
    return (
      this.cloneLayouts(this.persistedLayouts) ??
      this.readPersistedFromLocalStorage(this.grid.persist)
    );
  }

  private writePersisted(scope: LayoutPersistenceScope = 'USER'): void {
    if (!this.grid.persist) return;
    const snapshot = this.buildPersistedLayoutSnapshot();
    this.persistedLayouts = this.cloneLayouts(snapshot.layouts);
    if (scope === 'USER') {
      this.writePersistedToLocalStorage(snapshot);
    }
    this.writePersistedToApi(snapshot, scope);
  }

  private persistKey(): string {
    if (this.grid.persistKey) return `dashboard-grid:${this.grid.persistKey}`;
    const routePath = this.route.snapshot.routeConfig?.path ?? this.route.snapshot.url.map((s) => s.path).join('/');
    return `dashboard-grid:${this.sourceKey || routePath || 'dashboard'}`;
  }

  private updateRowsForResponsiveService(): void {
    const max = (this.layouts[this.currentBp] ?? []).reduce((m, i) => Math.max(m, i.y + i.h), 0);
    this.responsiveLayoutService.setRowCount(Math.max(1, Math.ceil(max / Math.max(1, this.grid.defaultH))));
  }

  private queueCompactFitMeasurement(): void {
    if (this.screenFloating) {
      this.responsiveLayoutService.setCompactLayoutFitOverride(null);
      return;
    }
    if (this.compactFitRafId !== null) {
      cancelAnimationFrame(this.compactFitRafId);
    }
    this.compactFitRafId = requestAnimationFrame(() => {
      this.compactFitRafId = requestAnimationFrame(() => {
        this.compactFitRafId = null;
        this.measureCompactFit();
      });
    });
  }

  private measureCompactFit(): void {
    if (this.screenFloating) {
      this.responsiveLayoutService.setCompactLayoutFitOverride(null);
      return;
    }

    const contentEl = this.dashboardContentRef?.nativeElement;
    const hostEl = this.hostRef.nativeElement;
    if (!contentEl || !hostEl) return;

    const viewportHost =
      hostEl.closest('mat-sidenav-content, .mat-sidenav-content') as HTMLElement | null;
    const viewportBottom = viewportHost?.getBoundingClientRect().bottom ?? window.innerHeight;
    const contentTop = contentEl.getBoundingClientRect().top;
    const availableHeight = Math.max(0, Math.floor(viewportBottom - contentTop));
    const browserZoomScale = window.visualViewport?.scale ?? 1;
    const requiredHeight = this.responsiveLayoutService.getEstimatedCompactRequiredHeight();
    const currentlyCompact = !this.effectiveScreenFloating;
    const currentSnapshot = this.getCompactViewportSnapshot(requiredHeight);
    const heightMargin = currentlyCompact
      ? DashboardComponent.COMPACT_EXIT_MARGIN_PX
      : DashboardComponent.COMPACT_ENTER_MARGIN_PX;

    let fits =
      browserZoomScale <= DashboardComponent.COMPACT_BASELINE_BROWSER_ZOOM_MAX &&
      availableHeight >= (requiredHeight + heightMargin);

    if (!currentlyCompact && this.compactScrollLock) {
      const locked = this.compactScrollLockSnapshot;
      const viewportImproved = !!locked && (
        currentSnapshot.width >= locked.width + 48 ||
        currentSnapshot.height >= locked.height + 48 ||
        currentSnapshot.scale <= locked.scale - 0.05
      );
      const heightDemandImproved = !!locked &&
        currentSnapshot.requiredHeight <= Math.max(0, locked.requiredHeight - 24);

      if (!viewportImproved && !heightDemandImproved) {
        fits = false;
      }
    }

    if (currentlyCompact) {
      const gridEl = this.gridContainerRef?.nativeElement;
      const contentOverflow = contentEl.scrollHeight > (contentEl.clientHeight + 2);
      const gridOverflow = !!gridEl && gridEl.scrollHeight > (contentEl.clientHeight + 2);
      const minChartHeight = this.findSmallestChartHostHeight(gridEl);
      const chartTooShort =
        minChartHeight > 0 &&
        minChartHeight < this.responsiveLayoutService.getCompactMinimumChartHeight();

      if (contentOverflow || gridOverflow || chartTooShort) {
        fits = false;
      }
    }

    if (!fits) {
      this.compactScrollLock = true;
      this.compactScrollLockSnapshot = currentSnapshot;
    } else if (currentlyCompact || this.compactScrollLock) {
      this.compactScrollLock = false;
      this.compactScrollLockSnapshot = null;
    }

    this.responsiveLayoutService.setCompactLayoutFitOverride(fits);
  }

  private findSmallestChartHostHeight(gridEl?: HTMLElement): number {
    if (!gridEl) return 0;

    let minHeight = Number.POSITIVE_INFINITY;
    const chartHosts = gridEl.querySelectorAll('.card-body > div[id]');
    chartHosts.forEach((el) => {
      const height = (el as HTMLElement).clientHeight;
      if (height > 0 && height < minHeight) {
        minHeight = height;
      }
    });

    return Number.isFinite(minHeight) ? minHeight : 0;
  }

  private getCompactViewportSnapshot(requiredHeight = this.responsiveLayoutService.getEstimatedCompactRequiredHeight()): CompactViewportSnapshot {
    return {
      width: window.innerWidth,
      height: window.innerHeight,
      scale: window.visualViewport?.scale ?? 1,
      requiredHeight,
    };
  }

  private releaseCompactScrollLockIfViewportImproved(): void {
    if (this.screenFloating || !this.compactScrollLock || !this.compactScrollLockSnapshot) return;

    const current = this.getCompactViewportSnapshot();
    const locked = this.compactScrollLockSnapshot;
    const widthImproved = current.width >= locked.width + 48;
    const heightImproved = current.height >= locked.height + 48;
    const scaleImproved = current.scale <= locked.scale - 0.05;
    const requiredHeightImproved = current.requiredHeight <= Math.max(0, locked.requiredHeight - 24);

    if (widthImproved || heightImproved || scaleImproved || requiredHeightImproved) {
      this.compactScrollLock = false;
      this.compactScrollLockSnapshot = null;
    }
  }

  private resolveBp(width: number): string { return this.orderedBps.find((bp) => width >= (this.grid.breakpoints[bp] ?? 0)) ?? 'lg'; }
  private cellW(): number { return (Math.max(0, this.containerWidth - (this.cols - 1) * this.grid.gap)) / Math.max(1, this.cols); }
  private spanToCols(span: number, cols: number): number { return Math.round((span / 12) * cols) || cols; }
  private resolveWidgetHeightForBreakpoint(widget: any, bp: string): unknown {
    if (!widget || typeof widget !== 'object') {
      return widget;
    }

    if ((bp === 'xxs' || bp === 'xs' || bp === 'sm') && widget.heightSm != null && widget.heightSm !== '') {
      return widget.heightSm;
    }

    if (bp === 'md' && widget.heightMd != null && widget.heightMd !== '') {
      return widget.heightMd;
    }

    if ((bp === 'lg' || bp === 'xl') && widget.heightLg != null && widget.heightLg !== '') {
      return widget.heightLg;
    }

    if (widget.height != null && widget.height !== '') {
      return widget.height;
    }

    return widget?.chartConfig?.height ?? this.responsiveLayoutService.getDefaultWidgetHeight();
  }
  private heightToRows(height: unknown): number {
    const px = this.responsiveLayoutService.resolveHeightToPixels(height) ?? 0;
    return px > 0
      ? Math.ceil((px + this.grid.gap) / (this.grid.rowHeight + this.grid.gap))
      : this.grid.defaultH;
  }
  private span(cls: unknown, bp: string): number {
    if (typeof cls !== 'string') return 12;
    const chains: Record<string, string[]> = { xl: ['xl', 'lg', 'md', 'sm', ''], lg: ['lg', 'md', 'sm', ''], md: ['md', 'sm', ''], sm: ['sm', ''], xs: [''], xxs: [''] };
    for (const key of chains[bp] ?? ['']) {
      const re = key ? new RegExp(`(?:^|\\s)col-${key}-(\\d{1,2})(?=\\s|$)`) : /(?:^|\s)col-(\d{1,2})(?=\s|$)/;
      const m = cls.match(re);
      if (m?.[1]) return this.clamp(Number(m[1]), 1, 12);
    }
    return 12;
  }
  private normalizeType(type: unknown): string {
    return typeof type === 'string' ? type.trim().toLowerCase() : '';
  }
  private firstEnabledFlag(...values: unknown[]): boolean | null {
    for (const value of values) {
      const enabled = this.toEnabledFlag(value);
      if (enabled !== null) return enabled;
    }
    return null;
  }
  private toEnabledFlag(value: unknown): boolean | null {
    if (typeof value === 'boolean') return value;
    if (!value || typeof value !== 'object') return null;

    const cfg = value as Record<string, unknown>;
    if (typeof cfg['enabled'] === 'boolean') return cfg['enabled'];
    if (typeof cfg['enable'] === 'boolean') return cfg['enable'];
    if (typeof cfg['visible'] === 'boolean') return cfg['visible'];
    if (typeof cfg['show'] === 'boolean') return cfg['show'];

    return null;
  }
  private mapStrings(v: unknown): string[] {
    if (!Array.isArray(v)) return [];
    const out = new Set<string>();
    for (const item of v) {
      const normalized = this.normalizeType(item);
      if (normalized) out.add(normalized);
    }
    return [...out];
  }
  private mapNumbers(v: any, fallback: Record<string, number>): Record<string, number> {
    if (!v || typeof v !== 'object') return { ...fallback };
    const out: Record<string, number> = {};
    for (const [k, val] of Object.entries(v)) if (typeof val === 'number' && Number.isFinite(val) && val >= 0) out[k] = val;
    return Object.keys(out).length ? out : { ...fallback };
  }
  private num(v: unknown, fb: number): number {
    const n = typeof v === 'number' ? v : Number(v);
    return Number.isFinite(n) && n > 0 ? Math.round(n) : fb;
  }
  private clamp(v: number, min: number, max: number): number { return Math.min(max, Math.max(min, Math.round(v))); }

  private isGridPersistenceEnabled(config: any): boolean {
    const gridConfig = config?.layoutConfig ?? config?.gridLayout ?? {};
    return gridConfig?.persist !== false;
  }

  private buildPersistedLayoutSnapshot(): PersistedLayoutSnapshot {
    return {
      version: this.grid.version,
      layouts: this.cloneLayouts(this.layouts) ?? {},
    };
  }

  private cloneLayouts(layouts: Record<string, GridItem[]> | null | undefined): Record<string, GridItem[]> | null {
    if (!layouts || typeof layouts !== 'object') return null;
    return JSON.parse(JSON.stringify(layouts));
  }

  private cloneJson<T>(value: T): T {
    return value == null ? value : JSON.parse(JSON.stringify(value));
  }

  private readPersistedFromLocalStorage(
    persistEnabled = this.grid.persist,
    ignoreActiveLayout = false
  ): Record<string, GridItem[]> | null {
    if (!persistEnabled) return null;
    if (!ignoreActiveLayout && this.layoutService.getActiveLayoutId()) return null;
    try {
      const raw = localStorage.getItem(this.persistKey());
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      return this.normalizePersistedLayouts(parsed);
    } catch {
      return null;
    }
  }

  private writePersistedToLocalStorage(snapshot: PersistedLayoutSnapshot): void {
    try {
      localStorage.setItem(this.persistKey(), JSON.stringify(snapshot));
    } catch { }
  }

  private loadPersistedLayouts(config: any): Observable<Record<string, GridItem[]> | null> {
    const fallback = this.readPersistedFromLocalStorage(
      this.isGridPersistenceEnabled(config),
      true,
    );
    const request = this.resolveLayoutPersistenceRequest(config, 'load');
    if (!request) {
      this.persistedLayoutRecord = null;
      return of(fallback);
    }

    const persistence = this.resolveLayoutPersistenceConfig(config);
    if (persistence && this.isEmailOwnedLayoutPersistence(persistence)) {
      return this.loadPersistedLayoutsByEmail(config, request, fallback);
    }

    return this.requestPersistedLayout(request, null, 'RESOLVE').pipe(
      map((response) => {
        this.persistedLayoutRecord = null;
        return this.extractPersistedLayouts(
          response,
          request.responsePath,
          this.resolveLayoutDashboardId(request),
        ) ?? fallback;
      }),
      catchError((err) => {
        console.error('Error loading persisted dashboard layout:', err);
        return of(fallback);
      }),
    );
  }

  private writePersistedToApi(
    snapshot: PersistedLayoutSnapshot,
    scope: LayoutPersistenceScope
  ): void {
    const request = this.resolveLayoutPersistenceRequest(this.configurations, 'save');
    if (!request) return;

    const persistence = this.resolveLayoutPersistenceConfig(this.configurations);
    if (persistence && this.isEmailOwnedLayoutPersistence(persistence)) {
      this.writePersistedToApiByEmail(snapshot, request, persistence);
      return;
    }

    this.subs.add(
      this.requestPersistedLayout(request, snapshot, scope)
        .pipe(
          catchError((err) => {
            console.error('Error saving dashboard layout:', err);
            return of(null);
          }),
        )
        .subscribe()
    );
  }

  private loadPersistedLayoutsByEmail(
    config: any,
    request: LayoutPersistenceRequestConfig,
    fallback: Record<string, GridItem[]> | null,
  ): Observable<Record<string, GridItem[]> | null> {
    const user = this.getCurrentLayoutUserContext();
    const currentEmail = user.email;
    if (!currentEmail) {
      this.persistedLayoutRecord = null;
      return of(fallback);
    }

    const lookupEmails = [
      currentEmail,
      ...this.resolveFallbackAdminEmails(config, currentEmail),
    ];

    return this.resolvePersistedLayoutsByEmail(request, lookupEmails, currentEmail, 0).pipe(
      map((result: PersistedLayoutLookupResult) => {
        this.persistedLayoutRecord = result.record;
        return result.layouts ?? fallback;
      }),
      catchError((err) => {
        console.error('Error loading persisted dashboard layout:', err);
        this.persistedLayoutRecord = null;
        return of(fallback);
      }),
    );
  }

  private resolvePersistedLayoutsByEmail(
    request: LayoutPersistenceRequestConfig,
    lookupEmails: string[],
    currentEmail: string,
    index: number,
  ): Observable<PersistedLayoutLookupResult> {
    if (index >= lookupEmails.length) {
      return of({
        layouts: null,
        record: null,
      });
    }

    const email = lookupEmails[index];
    const emailRequest = this.buildEmailLoadRequest(request, email);

    return this.requestPersistedLayout(emailRequest, null, 'RESOLVE').pipe(
      map((response) => {
        const dashboardId = this.resolveLayoutDashboardId(emailRequest);
        const record = this.extractPersistedLayoutRecord(response, dashboardId);
        if (!record) return null;

        const layouts = this.extractPersistedLayouts(
          response,
          emailRequest.responsePath,
          dashboardId,
        );
        if (!layouts) return null;

        return {
          layouts,
          record: this.normalizePersistedLayoutRecord(record, email, currentEmail),
        };
      }),
      catchError((err) => {
        console.error(`Error loading persisted dashboard layout for ${email}:`, err);
        return of(null);
      }),
      switchMap((result: PersistedLayoutLookupResult | null) => result
        ? of(result)
        : this.resolvePersistedLayoutsByEmail(request, lookupEmails, currentEmail, index + 1))
    );
  }

  private writePersistedToApiByEmail(
    snapshot: PersistedLayoutSnapshot,
    request: LayoutPersistenceRequestConfig,
    persistence: LayoutPersistenceConfig,
  ): void {
    const user = this.getCurrentLayoutUserContext();
    const email = user.email;
    if (!email) {
      console.error('Cannot save dashboard layout: logged-in user email is unavailable.');
      return;
    }

    const role = this.resolveEmailOwnedLayoutRole(persistence, email);
    const isCurrentUserRecord = this.isPersistedLayoutOwnedByEmail(email);
    const uuid = isCurrentUserRecord
      ? this.persistedLayoutRecord?.uuid ?? this.resolvePersistedLayoutUuid(this.persistedLayoutRecord?.raw) ?? null
      : null;
    const operation: 'insert' | 'update' = isCurrentUserRecord ? 'update' : 'insert';
    if (operation === 'update' && !uuid) {
      console.error('Cannot update dashboard layout: existing user layout was found but uuid is missing.');
      return;
    }

    const emailRequest = this.buildEmailSaveRequest(request, email, role, operation, uuid);

    this.subs.add(
      this.requestPersistedLayout(emailRequest, snapshot, 'USER')
        .pipe(
          tap((response) => this.capturePersistedLayoutRecordAfterSave(response, email, role, operation, emailRequest)),
          catchError((err) => {
            console.error('Error saving dashboard layout:', err);
            return of(null);
          }),
        )
        .subscribe()
    );
  }

  private resolveLayoutPersistenceRequest(
    config: any,
    mode: 'load' | 'save'
  ): LayoutPersistenceRequestConfig | null {
    const raw = this.resolveLayoutPersistenceConfig(config);
    if (!raw) return null;

    const { load, save, ...base } = raw;
    const override = mode === 'load' ? load : save;
    const endpoint = {
      ...base,
      ...(override && typeof override === 'object' ? override : {}),
    } as LayoutPersistenceRequestConfig;
    const apiUrl = String(endpoint.apiUrl ?? '').trim();

    if (endpoint.enabled === false || !apiUrl) return null;

    return {
      ...endpoint,
      apiUrl,
    };
  }

  private resolveLayoutPersistenceConfig(config: any): LayoutPersistenceConfig | null {
    const gridConfig = config?.layoutConfig ?? config?.gridLayout ?? {};
    const raw =
      config?.layoutPersistence ??
      gridConfig?.layoutPersistence;

    return raw && typeof raw === 'object'
      ? raw as LayoutPersistenceConfig
      : null;
  }

  private isEmailOwnedLayoutPersistence(config: LayoutPersistenceConfig | null): boolean {
    return config?.emailOwnerMode === true;
  }

  private buildEmailLoadRequest(
    request: LayoutPersistenceRequestConfig,
    email: string,
  ): LayoutPersistenceRequestConfig {
    const customPayload = this.cloneJson(request.customPayload ?? {}) as Record<string, unknown>;
    delete customPayload['requestData'];
    customPayload['email_id'] = [email];

    const dashboardId = this.resolveLayoutDashboardId(request);
    if (dashboardId) {
      customPayload['dashboard_id'] = [dashboardId];
    }

    return {
      ...request,
      includeRequestContext: false,
      skipDefaultPayload: true,
      requestData: {},
      defaultRequestData: {},
      customPayload,
    };
  }

  private buildEmailSaveRequest(
    request: LayoutPersistenceRequestConfig,
    email: string,
    role: 'Admin' | 'User',
    operation: 'insert' | 'update',
    uuid: string | null,
  ): LayoutPersistenceRequestConfig {
    const customPayload = this.cloneJson(request.customPayload ?? {}) as Record<string, unknown>;
    delete customPayload['requestData'];

    const requestData = this.cloneJson(request.requestData ?? request.defaultRequestData ?? {}) as Record<string, unknown>;
    requestData['email_id'] = email;

    const dashboardId = this.resolveLayoutDashboardId(request);
    if (dashboardId) {
      requestData['dashboard_id'] = dashboardId;
    }

    requestData['role'] = role;
    requestData['operation'] = operation;

    if (operation === 'update' && uuid) {
      requestData['uuid'] = uuid;
    } else {
      delete requestData['uuid'];
    }

    return {
      ...request,
      includeRequestContext: false,
      skipDefaultPayload: true,
      requestData,
      defaultRequestData: requestData,
      customPayload,
    };
  }

  private resolveLayoutDashboardId(request?: LayoutPersistenceRequestConfig | null): string | null {
    const direct = this.firstString(
      request?.dashboard_id,
      request?.dashboardId,
      typeof request?.requestData?.['dashboard_id'] === 'string' ? request.requestData['dashboard_id'] : null,
      typeof request?.requestData?.['dashboardId'] === 'string' ? request.requestData['dashboardId'] : null,
      typeof request?.defaultRequestData?.['dashboard_id'] === 'string' ? request.defaultRequestData['dashboard_id'] : null,
      typeof request?.defaultRequestData?.['dashboardId'] === 'string' ? request.defaultRequestData['dashboardId'] : null,
      this.configFileName,
      this.sourceKey,
    );

    return direct ? direct.trim() : null;
  }

  private extractPersistedLayoutRecords(response: any): any[] {
    if (Array.isArray(response)) {
      return response.filter(Boolean);
    }

    if (response?.responseList?.[0]?.data?.length) {
      return response.responseList[0].data.filter(Boolean);
    }

    if (Array.isArray(response?.data)) {
      return response.data.filter(Boolean);
    }

    return response ? [response] : [];
  }

  private extractPersistedLayoutRecord(
    response: any,
    dashboardId?: string | null,
  ): any | null {
    const records = this.extractPersistedLayoutRecords(response);
    if (!records.length) return response ?? null;

    const expectedDashboardId = this.normalizeDashboardId(dashboardId);
    if (!expectedDashboardId) {
      return records[0] ?? null;
    }

    return records.find((record) =>
      this.sameDashboardId(
        this.resolvePersistedLayoutDashboardId(record),
        expectedDashboardId,
      )
    ) ?? null;
  }

  private resolvePersistedLayoutDashboardId(record: any): string | null {
    const direct = this.firstString(
      record?.dashboard_id,
      record?.dashboardId,
      record?.requestData?.dashboard_id,
      record?.requestData?.dashboardId,
      record?.data?.dashboard_id,
      record?.data?.dashboardId,
    );

    return direct ? direct.trim() : null;
  }

  private normalizeDashboardId(value: string | null | undefined): string | null {
    const trimmed = typeof value === 'string' ? value.trim() : '';
    return trimmed || null;
  }

  private sameDashboardId(a: string | null | undefined, b: string | null | undefined): boolean {
    const left = this.normalizeDashboardId(a)?.toLowerCase();
    const right = this.normalizeDashboardId(b)?.toLowerCase();
    return !!left && !!right && left === right;
  }

  private normalizePersistedLayoutRecord(
    record: any,
    sourceEmail: string,
    currentEmail: string,
  ): PersistedLayoutRecord {
    return {
      emailId: this.firstString(record?.email_id, record?.emailId, record?.email) ?? sourceEmail,
      uuid: this.resolvePersistedLayoutUuid(record),
      role: this.firstString(record?.role, record?.scope_type),
      sourceEmail,
      isFallback: !this.sameEmail(sourceEmail, currentEmail),
      raw: record,
    };
  }

  private capturePersistedLayoutRecordAfterSave(
    response: any,
    email: string,
    role: 'Admin' | 'User',
    operation: 'insert' | 'update',
    request?: LayoutPersistenceRequestConfig | null,
  ): void {
    const savedRecord = this.extractPersistedLayoutRecord(response, this.resolveLayoutDashboardId(request));
    const uuid = this.resolvePersistedLayoutUuid(savedRecord)
      ?? (operation === 'update' ? (this.persistedLayoutRecord?.uuid ?? null) : null);

    this.persistedLayoutRecord = {
      emailId: this.firstString(savedRecord?.email_id, savedRecord?.emailId, savedRecord?.email) ?? email,
      uuid,
      role: this.firstString(savedRecord?.role, savedRecord?.scope_type) ?? role,
      sourceEmail: email,
      isFallback: false,
      raw: savedRecord,
    };
  }

  private resolvePersistedLayoutUuid(record: any): string | null {
    const direct = this.firstString(
      record?.uuid,
      record?.UUID,
      record?.layout_uuid,
      record?.layoutUuid,
      record?.requestData?.uuid,
      record?.data?.uuid,
    );
    if (direct) return direct;

    const stringId = typeof record?.id === 'string' ? record.id.trim() : '';
    return this.isUuidLike(stringId) ? stringId : null;
  }

  private resolveFallbackAdminEmails(
    config: any,
    currentEmail?: string | null,
  ): string[] {
    const persistence = this.resolveLayoutPersistenceConfig(config);
    const configured = [
      ...(Array.isArray(persistence?.fallbackAdminEmails) ? persistence!.fallbackAdminEmails : []),
      ...(typeof persistence?.fallbackAdminEmail === 'string' ? [persistence.fallbackAdminEmail] : []),
    ];
    const source = configured.length
      ? configured
      : (Array.isArray(persistence?.adminEmails) ? persistence!.adminEmails : []);
    const current = this.normalizeEmail(currentEmail);
    const seen = new Set<string>();
    const emails: string[] = [];

    for (const value of source) {
      const trimmed = String(value || '').trim();
      const normalized = this.normalizeEmail(trimmed);
      if (!trimmed || !normalized || normalized === current || seen.has(normalized)) continue;
      seen.add(normalized);
      emails.push(trimmed);
    }

    return emails;
  }

  private resolveEmailOwnedLayoutRole(
    config: LayoutPersistenceConfig | null,
    email: string,
  ): 'Admin' | 'User' {
    const normalized = this.normalizeEmail(email);
    const adminEmails = new Set(
      (config?.adminEmails || [])
        .map((value) => this.normalizeEmail(value))
        .filter((value): value is string => !!value)
    );

    return normalized && adminEmails.has(normalized) ? 'Admin' : 'User';
  }

  private isPersistedLayoutOwnedByEmail(email: string): boolean {
    if (!this.persistedLayoutRecord || this.persistedLayoutRecord.isFallback) return false;

    return this.sameEmail(
      this.persistedLayoutRecord.emailId ?? this.persistedLayoutRecord.sourceEmail,
      email,
    );
  }

  private normalizeEmail(value: unknown): string | null {
    return typeof value === 'string' && value.trim()
      ? value.trim().toLowerCase()
      : null;
  }

  private isUuidLike(value: string): boolean {
    return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value);
  }

  private sameEmail(left: unknown, right: unknown): boolean {
    const a = this.normalizeEmail(left);
    const b = this.normalizeEmail(right);
    return !!a && !!b && a === b;
  }

  private requestPersistedLayout(
    request: LayoutPersistenceRequestConfig,
    snapshot?: PersistedLayoutSnapshot | null,
    mode: LayoutPersistenceRequestMode = 'RESOLVE'
  ) {
    const method = String(request.method ?? 'post').trim().toLowerCase() || 'post';
    const layoutKey = String(request.layoutKey ?? 'layout').trim() || 'layout';
    const requestContext = request.includeRequestContext === false
      ? {}
      : this.buildLayoutPersistenceRequestContext(mode);
    const defaultRequestData = this.cloneJson(
      {
        ...(request.requestData ?? request.defaultRequestData ?? {}),
        ...requestContext,
      }
    ) as Record<string, unknown>;
    const customPayload = this.cloneJson(request.customPayload ?? {}) as Record<string, unknown>;
    const data: Record<string, unknown> = {
      ...request,
      headers: request.headers,
      useQueryParam: request.useQueryParams === true,
    };

    if (request.skipDefaultPayload) {
      const payload =
        customPayload && typeof customPayload === 'object' ? customPayload : {};
      const payloadRequestData =
        payload['requestData'] && typeof payload['requestData'] === 'object'
          ? payload['requestData'] as Record<string, unknown>
          : {};
      const mergedRequestData = {
        ...defaultRequestData,
        ...payloadRequestData,
      };

      if (Object.keys(mergedRequestData).length) {
        payload['requestData'] = mergedRequestData;
      }

      data['customPayload'] = payload;
      data['skipDefaultPayload'] = true;
    } else {
      data['defaultRequestData'] = defaultRequestData;
      if (Object.keys(customPayload).length) {
        data['customPayload'] = customPayload;
      }
    }

    return this.configService.getConfig(
      request.apiUrl!,
      data,
      {
        requestParams: snapshot ? { [layoutKey]: snapshot } : {},
        queryParams: this.cloneJson(request.queryParams ?? {}),
        useQueryParams: request.useQueryParams === true,
        headers: request.headers,
      },
      method,
    );
  }

  private buildLayoutPersistenceRequestContext(
    mode: LayoutPersistenceRequestMode
  ): Record<string, unknown> {
    const user = this.getCurrentLayoutUserContext();
    const context: Record<string, unknown> = {
      scopeType: mode,
      resolveScopes: ['USER', 'GLOBAL'],
      roleIds: user.roleIds,
    };

    if (user.userId) {
      context['userId'] = user.userId;
    }

    if (user.username) {
      context['username'] = user.username;
    }

    if (mode === 'USER') {
      context['scopeId'] = user.userId || 'anonymous';
    } else if (mode === 'GLOBAL') {
      context['scopeId'] = 'GLOBAL';
    }

    if (mode !== 'RESOLVE') {
      delete context['resolveScopes'];
    }

    return context;
  }

  private getCurrentLayoutUserContext(): LayoutUserContext {
    const keycloak = this.keycloakService.getKeycloakInstance();
    const tokenParsed: any = keycloak?.tokenParsed ?? {};
    const realmRoles = Array.isArray(tokenParsed?.realm_access?.roles)
      ? tokenParsed.realm_access.roles
      : [];
    const resourceRoles = Object.values(tokenParsed?.resource_access ?? {})
      .flatMap((resource: any) => Array.isArray(resource?.roles) ? resource.roles : []);
    const roleIds = [...new Set(
      [...realmRoles, ...resourceRoles]
        .map((role) => String(role || '').trim())
        .filter((role) => !!role)
    )];

    return {
      userId: this.firstString(tokenParsed?.sub, keycloak?.subject),
      username: this.firstString(
        tokenParsed?.preferred_username,
        tokenParsed?.name
      ),
      email: this.firstString(
        tokenParsed?.email,
        typeof tokenParsed?.preferred_username === 'string' && tokenParsed.preferred_username.includes('@')
          ? tokenParsed.preferred_username
          : null,
      ),
      roleIds,
    };
  }

  private saveLayout(scope: LayoutPersistenceScope): void {
    if (!this.editMode) return;

    if (this.interaction) {
      this.finishInteraction(false);
    }

    this.writePersisted(scope);
    this.syncDashboardAfterLayoutChange();
    this.exitEditMode();
  }

  private exitEditMode(): void {
    this.editMode = false;
    this.activeDragId = null;
    this.activeResizeId = null;
    this.editSessionLayouts = null;
  }

  private syncDashboardAfterLayoutChange(): void {
    this.updateRowsForResponsiveService();
    this.queueCompactFitMeasurement();
    requestAnimationFrame(() => this.responsiveLayoutService.triggerReflow());
  }

  private extractPersistedLayouts(
    response: any,
    responsePath?: string,
    dashboardId?: string | null,
  ): Record<string, GridItem[]> | null {
    const matchedRecord = dashboardId
      ? this.extractPersistedLayoutRecord(response, dashboardId)
      : null;
    if (dashboardId && !matchedRecord) return null;

    const source = matchedRecord ?? response;
    const preferred = responsePath
      ? this.readPathValue(source, responsePath)
      : source;
    const candidates = [
      preferred,
      preferred?.layouts,
      preferred?.layout,
      preferred?.layout_json,
      preferred?.gridLayout,
      preferred?.savedLayout,
      preferred?.savedLayouts,
      source?.layouts,
      source?.layout,
      source?.layout_json,
      source?.gridLayout,
      source?.savedLayout,
      source?.savedLayouts,
      source?.requestData?.layout,
      source?.requestData?.layouts,
      source?.requestData?.layout_json,
      source?.data?.layout,
      source?.data?.layouts,
      source?.data?.layout_json,
      response?.layouts,
      response?.layout,
      response?.layout_json,
      response?.gridLayout,
      response?.savedLayout,
      response?.savedLayouts,
      response?.requestData?.layout,
      response?.requestData?.layouts,
      response?.requestData?.layout_json,
      response?.data?.layout,
      response?.data?.layouts,
      response?.data?.layout_json,
    ];

    for (const candidate of candidates) {
      const normalized = this.normalizePersistedLayouts(candidate);
      if (normalized) return normalized;
    }

    return null;
  }

  private normalizePersistedLayouts(candidate: any): Record<string, GridItem[]> | null {
    if (!candidate) return null;

    let parsed = candidate;
    if (typeof parsed === 'string') {
      try {
        parsed = JSON.parse(parsed);
      } catch {
        return null;
      }
    }

    const rawLayouts =
      parsed?.layouts && typeof parsed.layouts === 'object'
        ? parsed.layouts
        : parsed;

    if (!rawLayouts || typeof rawLayouts !== 'object' || Array.isArray(rawLayouts)) {
      return null;
    }

    const entries = Object.entries(rawLayouts)
      .filter(([, value]) => Array.isArray(value));

    if (!entries.length) return null;

    return entries.reduce((acc, [bp, value]) => {
      acc[bp] = (value as Array<Record<string, unknown>>)
        .map((item) => ({ ...item } as unknown as GridItem));
      return acc;
    }, {} as Record<string, GridItem[]>);
  }

  private readPathValue(source: any, path?: string): any {
    if (!path?.trim()) return source;

    return path
      .split('.')
      .map((segment) => segment.trim())
      .filter((segment) => !!segment)
      .reduce((current: any, segment) => {
        if (current == null) return undefined;
        if (Array.isArray(current) && /^\d+$/.test(segment)) {
          return current[Number(segment)];
        }
        return current?.[segment];
      }, source);
  }
}
